import { EventType, SleepStage } from '@/common/types';

const respiratoryEventType = (base: EventType, signalName: string): string =>
  signalName === 'Nasal_pressure' ? base : `${base} ${signalName}`;

const channelEventType = (lMember: string, rMember: string, signalName: string): string =>
  signalName.endsWith('_R') ? rMember : lMember;

interface EventShortcutAction {
  id: string;
  members: string[];
  defaultKey?: string;
  resolve: (signalName: string) => string;
}

export const EVENT_SHORTCUT_ACTIONS: EventShortcutAction[] = [
  {
    id: 'apnea-central',
    members: [EventType.APNEA_CENTRAL, EventType.APNEA_CENTRAL_RESP_FLOW],
    defaultKey: 'c',
    resolve: (signalName) => respiratoryEventType(EventType.APNEA_CENTRAL, signalName)
  },
  {
    id: 'apnea-mixed',
    members: [EventType.APNEA_MIXED, EventType.APNEA_MIXED_RESP_FLOW],
    defaultKey: 'm',
    resolve: (signalName) => respiratoryEventType(EventType.APNEA_MIXED, signalName)
  },
  {
    id: 'apnea-obstructive',
    members: [EventType.APNEA_OBSTRUCTIVE, EventType.APNEA_OBSTRUCTIVE_RESP_FLOW],
    defaultKey: 'o',
    resolve: (signalName) => respiratoryEventType(EventType.APNEA_OBSTRUCTIVE, signalName)
  },
  {
    id: 'hypopnea-obstructive',
    members: [EventType.HYPOPNEA_OBSTRUCTIVE, EventType.HYPOPNEA_OBSTRUCTIVE_RESP_FLOW],
    defaultKey: 'h',
    resolve: (signalName) => respiratoryEventType(EventType.HYPOPNEA_OBSTRUCTIVE, signalName)
  },
  {
    id: 'hypopnea-central',
    members: [EventType.HYPOPNEA_CENTRAL, EventType.HYPOPNEA_CENTRAL_RESP_FLOW],
    defaultKey: 'g',
    resolve: (signalName) => respiratoryEventType(EventType.HYPOPNEA_CENTRAL, signalName)
  },
  {
    id: 'cheyne-stokes',
    members: [EventType.CHEYNE_STOKES_BREATHING],
    defaultKey: 'z',
    resolve: () => EventType.CHEYNE_STOKES_BREATHING
  },
  {
    id: 'arousal',
    members: [EventType.AROUSAL_FRONTAL_L, EventType.AROUSAL_FRONTAL_R],
    defaultKey: 'a',
    resolve: (signalName) =>
      channelEventType(EventType.AROUSAL_FRONTAL_L, EventType.AROUSAL_FRONTAL_R, signalName)
  },
  {
    id: 'leg-movement',
    members: [EventType.LEG_MOVEMENT],
    defaultKey: 'l',
    resolve: () => EventType.LEG_MOVEMENT
  },
  {
    id: 'periodic-leg-movement',
    members: [EventType.PERIODIC_LEG_MOVEMENT],
    defaultKey: 'p',
    resolve: () => EventType.PERIODIC_LEG_MOVEMENT
  },
  {
    id: 'desaturation',
    members: [EventType.DESATURATION],
    defaultKey: 'd',
    resolve: () => EventType.DESATURATION
  },
  {
    id: 'rera',
    members: [EventType.RERA, EventType.RERA_RESP_FLOW],
    defaultKey: 'u',
    resolve: (signalName) => respiratoryEventType(EventType.RERA, signalName)
  },
  {
    id: 'tachycardia',
    members: [EventType.TACHYCARDIA],
    defaultKey: 't',
    resolve: () => EventType.TACHYCARDIA
  },
  {
    id: 'bradycardia',
    members: [EventType.BRADYCARDIA],
    defaultKey: 'b',
    resolve: () => EventType.BRADYCARDIA
  },
  {
    id: 'snoring',
    members: [EventType.SNORING],
    defaultKey: 's',
    resolve: () => EventType.SNORING
  },
  {
    id: 'artifact',
    members: [],
    defaultKey: 'x',
    resolve: (signalName) => `Artifact ${signalName}`
  },
  {
    id: 'bruxism-phasic',
    members: [EventType.BRUXISM_PHASIC_L, EventType.BRUXISM_PHASIC_R],
    resolve: (signalName) =>
      channelEventType(EventType.BRUXISM_PHASIC_L, EventType.BRUXISM_PHASIC_R, signalName)
  },
  {
    id: 'bruxism-tonic',
    members: [EventType.BRUXISM_TONIC_L, EventType.BRUXISM_TONIC_R],
    resolve: (signalName) =>
      channelEventType(EventType.BRUXISM_TONIC_L, EventType.BRUXISM_TONIC_R, signalName)
  },
  {
    id: 'bruxism-mixed',
    members: [EventType.BRUXISM_MIX_L, EventType.BRUXISM_MIX_R],
    resolve: (signalName) =>
      channelEventType(EventType.BRUXISM_MIX_L, EventType.BRUXISM_MIX_R, signalName)
  },
  ...[
    EventType.ATRIAL_FIBRILLATION,
    EventType.PAC,
    EventType.PVC,
    EventType.AV_BLOCK,
    EventType.PACEMAKER_RHYTHM,
    EventType.OTHER_ARRHYTHMIA,
    EventType.ASYSTOLE,
    EventType.NARROW_COMPLEX_TACHYCARDIA,
    EventType.WIDE_COMPLEX_TACHYCARDIA
  ].map((type) => ({ id: type as string, members: [type], resolve: () => type }))
];

export const EVENT_TYPE_TO_SHORTCUT_ID: Record<string, string> = EVENT_SHORTCUT_ACTIONS.reduce(
  (acc, action) => {
    action.members.forEach((member) => {
      acc[member] = action.id;
    });
    return acc;
  },
  {} as Record<string, string>
);

export const keyboardShortcutMapping = (
  d3Client: any,
  signalName: string,
  effectiveShortcuts: Record<string, string> = {}
) => {
  const map: Record<string, () => void> = {
    '1': () => d3Client.updateEventByType(SleepStage.N1),
    '2': () => d3Client.updateEventByType(SleepStage.N2),
    '3': () => d3Client.updateEventByType(SleepStage.N3),
    '5': () => d3Client.updateEventByType(SleepStage.REM),
    '0': () => d3Client.updateEventByType(SleepStage.Wake),
    ' ': () => d3Client.updateEventByType(SleepStage.Unstaged),

    Delete: () => d3Client.deleteHoveredEvent(),
    Backspace: () => d3Client.deleteHoveredEvent()
  };

  EVENT_SHORTCUT_ACTIONS.forEach((action) => {
    const key = effectiveShortcuts[action.id] ?? action.defaultKey;

    if (!key) return;

    map[key] = () => d3Client.insertEventByType(action.resolve(signalName));
  });

  return map;
};

export const keyboardZoomMapping = (chartContextMenu: any) => ({
  '+': () => chartContextMenu.plusZoom?.(),
  '-': () => chartContextMenu.minusZoom?.(),
  '*': () => chartContextMenu.fitToScreenZoom?.(),
  f: () => chartContextMenu.fitAllToPane?.(),
  r: () => chartContextMenu?.resetHoveredToDefault?.(),
  o: () => chartContextMenu?.resetAllToDefault?.(),
  ArrowUp: () => chartContextMenu.upZoom?.(),
  ArrowDown: () => chartContextMenu.downZoom?.()
});

export const sleepStageKeyMap: Partial<Record<string, string>> = {
  '0': SleepStage.Wake,
  '1': SleepStage.N1,
  '2': SleepStage.N2,
  '3': SleepStage.N3,
  '5': SleepStage.REM
};
